/*
 * Central dayjs configuration.
 *
 * Replaces the old `moment` singleton (and `moment-customizations`). All app
 * code should import dayjs from here so the same plugins and locale
 * customizations are applied everywhere.
 */
import dayjs from 'dayjs'

// Plugins needed to preserve the moment API surface used across the app.
import localizedFormat from 'dayjs/plugin/localizedFormat' // L, LL, LT, l, ll ... tokens
import weekOfYear from 'dayjs/plugin/weekOfYear' // .week()
import advancedFormat from 'dayjs/plugin/advancedFormat' // `w` format token (needs weekOfYear)
import relativeTime from 'dayjs/plugin/relativeTime' // .fromNow()
import duration from 'dayjs/plugin/duration' // dayjs.duration()
import utc from 'dayjs/plugin/utc' // dayjs.utc() / .utc()
import customParseFormat from 'dayjs/plugin/customParseFormat' // dayjs(str, 'DD.MM.YYYY')
import updateLocale from 'dayjs/plugin/updateLocale' // dayjs.updateLocale()
import localeData from 'dayjs/plugin/localeData' // dayjs.weekdaysShort() / .months() etc.

// Only short language codes reach dayjs.locale() (app.js strips the region), so
// a region locale like fr-ch would never activate.
import 'dayjs/locale/de'
import 'dayjs/locale/fr'

dayjs.extend(localizedFormat)
dayjs.extend(weekOfYear)
dayjs.extend(advancedFormat)
dayjs.extend(relativeTime)
dayjs.extend(duration)
dayjs.extend(utc)
dayjs.extend(customParseFormat)
dayjs.extend(updateLocale)
dayjs.extend(localeData)

// German long-date customizations (previously in moment-customizations.js).
// dayjs' localizedFormat reads these from the locale `formats` object.
// LLL/LLLL are written out in full (dayjs does not recursively expand the LT
// reference the way moment's longDateFormat did).
dayjs.updateLocale('de', {
  formats: {
    LT: 'H:mm',
    L: 'DD.MM.YYYY',
    LL: 'D. MMMM YYYY',
    LLL: 'D. MMMM YYYY H:mm',
    LLLL: 'dddd, D. MMMM YYYY H:mm',
  },
})

export type Dayjs = dayjs.Dayjs
export default dayjs
